/**
 * ClarifiExportButton Component
 *
 * Button component for exporting loan data to Clarifi data tape format.
 * Supports single loan export with Excel download.
 */

import { useState } from 'react';
import { Download, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useClarifiExport } from '@/hooks/useClarifiExport';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

interface ClarifiExportButtonProps {
  /** The loan number to export (e.g., 'LW25120090') */
  loanNumber: string;
  /** Optional additional CSS classes */
  className?: string;
  /** Button variant */
  variant?: 'default' | 'outline' | 'ghost' | 'secondary';
  /** Button size */
  size?: 'default' | 'sm' | 'lg' | 'icon';
  /** Show label text or icon only */
  showLabel?: boolean;
}

/**
 * Button component for exporting a single loan to Clarifi data tape format.
 *
 * @example
 * ```tsx
 * // In loan detail view
 * <ClarifiExportButton loanNumber="LW25120090" />
 *
 * // Icon-only in toolbar
 * <ClarifiExportButton
 *   loanNumber={selectedLoan.loanNumber}
 *   showLabel={false}
 *   size="icon"
 * />
 * ```
 */
export function ClarifiExportButton({
  loanNumber,
  className,
  variant = 'outline',
  size = 'default',
  showLabel = true
}: ClarifiExportButtonProps) {
  const { exportLoan, isExporting, error } = useClarifiExport();
  const [showSuccess, setShowSuccess] = useState(false);

  const handleExport = async () => {
    const result = await exportLoan(loanNumber);

    if (result.success) {
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    }
  };

  const buttonContent = () => {
    if (isExporting) {
      return (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          {showLabel && <span className="ml-2">Exporting...</span>}
        </>
      );
    }

    if (showSuccess) {
      return (
        <>
          <CheckCircle className="h-4 w-4 text-green-500" />
          {showLabel && <span className="ml-2">Downloaded!</span>}
        </>
      );
    }

    if (error) {
      return (
        <>
          <AlertCircle className="h-4 w-4 text-red-500" />
          {showLabel && <span className="ml-2">Export Failed</span>}
        </>
      );
    }

    return (
      <>
        <Download className="h-4 w-4" />
        {showLabel && <span className="ml-2">Export to Clarifi</span>}
      </>
    );
  };

  const button = (
    <Button
      variant={variant}
      size={size}
      onClick={handleExport}
      disabled={isExporting || !loanNumber}
      className={cn(
        className,
        showSuccess && 'border-green-500 text-green-600',
        error && !isExporting && 'border-red-300 text-red-600'
      )}
    >
      {buttonContent()}
    </Button>
  );

  // Wrap in tooltip if showing icon only
  if (!showLabel) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent>
            <p>Export to Clarifi</p>
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return button;
}

/**
 * Compact version for table rows or tight spaces
 */
export function ClarifiExportIconButton({
  loanNumber,
  className
}: Pick<ClarifiExportButtonProps, 'loanNumber' | 'className'>) {
  return (
    <ClarifiExportButton
      loanNumber={loanNumber}
      showLabel={false}
      size="icon"
      variant="ghost"
      className={cn('h-8 w-8', className)}
    />
  );
}
